/**
 * coinbase-api.js
 *
 * Client-side Coinbase Advanced Trade API service.
 * In VPS mode: routes through Express proxy (localhost:3001/api/coinbase/*)
 * In dev/netlify: calls Coinbase public market API directly (CORS supported)
 *
 * Coinbase candle format (newest first):
 *   { start: "1782604800", low: "61.38", high: "63.46", open: "61.61", close: "62.87", volume: "207765" }
 *
 * Output: normalised to Binance-compatible raw kline arrays (oldest first):
 *   [openTime_ms, open, high, low, close, baseVolume, closeTime_ms, quoteVolume]
 */

import { getCoinbaseProxyBase } from './api-config';

function getCoinbaseBase() {
  return getCoinbaseProxyBase();
}

// --- Helpers ------------------------------------------------------------------

function normCandle(c) {
  const startMs  = parseInt(c.start) * 1000;
  const close    = parseFloat(c.close);
  const baseVol  = parseFloat(c.volume);
  const quoteVol = baseVol * close;
  return [
    startMs,
    c.open,
    c.high,
    c.low,
    c.close,
    c.volume,
    startMs + 3600000,
    String(quoteVol),
  ];
}

function resample1Hto4H(hourlyKlines) {
  const out = [];
  for (let i = 0; i < hourlyKlines.length; i += 4) {
    const block  = hourlyKlines.slice(i, i + 4);
    if (block.length === 0) continue;
    const opens  = block.map(k => parseFloat(k[1]));
    const highs  = block.map(k => parseFloat(k[2]));
    const lows   = block.map(k => parseFloat(k[3]));
    const closes = block.map(k => parseFloat(k[4]));
    const bVols  = block.map(k => parseFloat(k[5]));
    const qVols  = block.map(k => parseFloat(k[7]));
    const openMs = block[0][0];
    out.push([
      openMs,
      String(opens[0]),
      String(Math.max(...highs)),
      String(Math.min(...lows)),
      String(closes[closes.length - 1]),
      String(bVols.reduce((s, v) => s + v, 0)),
      String(openMs + 4 * 3600000),
      String(qVols.reduce((s, v) => s + v, 0)),
    ]);
  }
  return out;
}

function resampleDailyToWeekly(dailyKlines) {
  const groups = {};
  dailyKlines.forEach(function(k) {
    const date   = new Date(k[0]);
    const dow    = date.getUTCDay();
    const offset = dow === 0 ? 6 : dow - 1;
    const monday = new Date(date);
    monday.setUTCDate(date.getUTCDate() - offset);
    monday.setUTCHours(0, 0, 0, 0);
    const key = monday.getTime();
    if (!groups[key]) groups[key] = [];
    groups[key].push(k);
  });
  return Object.keys(groups)
    .map(Number)
    .sort(function(a, b) { return a - b; })
    .map(function(weekMs) {
      const block = groups[weekMs];
      const highs = block.map(function(k) { return parseFloat(k[2]); });
      const lows  = block.map(function(k) { return parseFloat(k[3]); });
      const bVols = block.map(function(k) { return parseFloat(k[5]); });
      const qVols = block.map(function(k) { return parseFloat(k[7]); });
      return [
        weekMs,
        block[0][1],
        String(Math.max.apply(null, highs)),
        String(Math.min.apply(null, lows)),
        block[block.length - 1][4],
        String(bVols.reduce(function(s, v) { return s + v; }, 0)),
        String(weekMs + 7 * 86400000),
        String(qVols.reduce(function(s, v) { return s + v; }, 0)),
      ];
    });
}

// --- Core fetch ---------------------------------------------------------------

async function _fetchCandlesRaw(productId, granularity, limit) {
  try {
    const params = new URLSearchParams({ granularity: granularity, limit: String(limit) });
    const url    = getCoinbaseBase() + '/products/' + productId + '/candles?' + params;
    const res    = await fetch(url);
    if (!res.ok) {
      const body = await res.json().catch(function() { return {}; });
      console.warn('[Coinbase] ' + productId + ' ' + granularity + ': HTTP ' + res.status + ' ' + (body.message || ''));
      return null;
    }
    const data = await res.json();
    if (!data.candles || data.candles.length === 0) {
      console.warn('[Coinbase] ' + productId + ' ' + granularity + ': 0 candles returned');
      return null;
    }
    return data.candles.slice().reverse().map(normCandle);
  } catch (err) {
    console.error('[Coinbase] ' + productId + ' ' + granularity + ':', err.message);
    return null;
  }
}

/**
 * Fetch candles for a Coinbase product.
 * Automatically tries the alternate quote currency (-USD <-> -USDC) on failure.
 *
 * @param {string} productId   e.g. "HYPE-USD" or "HYPE-USDC"
 * @param {string} granularity e.g. "ONE_HOUR", "ONE_DAY"
 * @param {number} limit       max candles (Coinbase cap: 350)
 * @returns {Promise<Array[]|null>}
 */
export async function fetchCoinbaseCandles(productId, granularity, limit) {
  const result = await _fetchCandlesRaw(productId, granularity, limit);
  if (result) return result;

  // Auto-fallback: swap -USD <-> -USDC
  var altId = null;
  if      (productId.endsWith('-USDC')) altId = productId.slice(0, -5) + '-USD';
  else if (productId.endsWith('-USD'))  altId = productId.slice(0, -4) + '-USDC';

  if (altId) {
    console.log('[Coinbase] Retrying ' + productId + ' -> ' + altId);
    return await _fetchCandlesRaw(altId, granularity, limit);
  }
  return null;
}

/**
 * Fetch all timeframes for a Coinbase product.
 * Returns { fourHour, daily, weekly } in Binance-compatible raw kline format.
 *
 * fourHour: fetches 1H candles (350 max) and resamples -> 4H (~87 candles)
 * daily:    fetches ONE_DAY (200 candles)
 * weekly:   resampled from daily
 *
 * @param {string} productId  e.g. "HYPE-USD"
 * @returns {Promise<{fourHour, daily, weekly}>}
 */
export async function fetchCoinbaseKlinesAllTimeframes(productId) {
  const [hourlyRaw, dailyRaw] = await Promise.all([
    fetchCoinbaseCandles(productId, 'ONE_HOUR', 350),
    fetchCoinbaseCandles(productId, 'ONE_DAY',  200),
  ]);
  return {
    fourHour: hourlyRaw ? resample1Hto4H(hourlyRaw) : null,
    daily:    dailyRaw,
    weekly:   dailyRaw ? resampleDailyToWeekly(dailyRaw) : null,
  };
}
